/**
 * Maps raw WebSocket broadcast events into human-friendly notification entries
 * for the header inbox. Events look like: { type, action, id | purchase_id, ... }.
 */

// Low-signal / plumbing frames we never surface in the inbox.
export const NOISE_TYPES = new Set([
  "pong",
  "ping",
  "hello",
  "hello_ack",
  "__ws_connected__",
  "ws_group",
  "live_scale",
  "WEIGHT_TICK",
  "CAMERA_PREVIEW",
  "CAPTURE_RESULT",
  "CAPTURE_REQUEST",
  "tally_status",
  "tally_status_ack",
  "tally_agent_welcome",
  "APPROVAL_QUEUE_COUNT_UPDATE",
  "access",
  "boolean",
  "string",
  "error",
  "lookup",
  "production_monitoring",
  "ok",
  "hash",
  "hot",
  "liveReload",
  "reconnect",
  "overlay",
  "invalid",
  "warnings",
  "errors",
  "static-changed",
  "content-changed",
  "log-status",
  "progress",
  "close",
]);

const TALLY_NOISE_ACTIONS = new Set(["agent_connected", "agent_disconnected"]);

const TYPE_META = {
  gate_pass: { noun: "Gate pass", iconKey: "gate", tone: "gold" },
  purchase: { noun: "Purchase", iconKey: "purchases", tone: "sage" },
  outward_dispatch: { noun: "Outward dispatch", iconKey: "purchases", tone: "sage" },
  lab: { noun: "Lab", iconKey: "lab", tone: "warning" },
  stack: { noun: "Unloading", iconKey: "stacks", tone: "sage" },
  contract: { noun: "Purchase contract", iconKey: "contracts", tone: "info" },
  sales_contract: { noun: "Sales contract", iconKey: "contracts", tone: "info" },
  voucher: { noun: "Voucher", iconKey: "accounts", tone: "gold" },
  production: { noun: "Production", iconKey: "production", tone: "sage" },
  production_batch: { noun: "Production batch", iconKey: "production", tone: "sage" },
  production_foreman: { noun: "Production", iconKey: "production", tone: "sage" },
  boiler_logging: { noun: "Boiler", iconKey: "production", tone: "sage" },
  party: { noun: "Party", iconKey: "parties", tone: "default" },
  bank_statement: { noun: "Bank statement", iconKey: "accounts", tone: "info" },
  tally: { noun: "Tally", iconKey: "accounts", tone: "info" },
  tally_sync_job: { noun: "Tally", iconKey: "accounts", tone: "info" },
  background_job: { noun: "Background task", iconKey: "accounts", tone: "info" },
  store_grn: { noun: "Store GRN", iconKey: "purchases", tone: "sage" },
};

const PURCHASE_STATE_LABEL = {
  REGISTERED: "registered at gate",
  WEIGH_GROSS: "awaiting gross weigh",
  LAB_PENDING: "sent to lab",
  BARGAINING: "in bargaining",
  APPROVED: "approved for unloading",
  UNLOADING: "unloading on floor",
  UNLOADING_HOLD: "unloading on hold",
  WEIGH_MID: "mid-weigh pending",
  WEIGH_TARE: "tare weigh pending",
  ACCOUNTS_PENDING: "awaiting accounts settlement",
  COMPLETED: "accounts completed",
  REJECTED: "rejected",
};

const OUTWARD_STATE_LABEL = {
  REGISTERED: "awaiting sales link",
  SALES_LINKED: "sales linked — tare pending",
  WEIGH_TARE: "awaiting loading",
  LOADED: "loaded — gross weigh pending",
  WEIGH_GROSS: "gross recorded",
  ACCOUNTS_PENDING: "awaiting sales voucher",
  COMPLETED: "completed",
  REJECTED: "rejected",
};

const VOUCHER_TYPE_LABEL = {
  MATERIAL: "Material",
  FREIGHT: "Freight",
  TDS: "TDS",
  SALES: "Sales",
  JOURNAL: "Journal",
  CASH: "Cash",
};

function humanize(value) {
  if (!value) return "";
  return String(value).replace(/_/g, " ").trim();
}

function titleCase(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function withQuery(base, key, value) {
  if (value == null || value === "") return base;
  const sep = String(base).includes("?") ? "&" : "?";
  return `${base}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

function gateModuleForPurpose(purpose) {
  const p = String(purpose || "").toUpperCase();
  if (p === "OUTWARD_RICE") return "outward";
  if (p === "INWARD_STORE") return "store";
  if (p === "VISITOR") return "visitors";
  return "procurement";
}

function purchaseId(event) {
  return event.purchase_id ?? (event.type === "purchase" ? event.id : null);
}

function gatePassId(event) {
  return event.gate_pass_id ?? (event.type === "gate_pass" ? event.id : null);
}

function dispatchId(event) {
  return event.dispatch_id ?? (event.type === "outward_dispatch" ? event.id : null);
}

function entityId(event) {
  return event.id ?? event.purchase_id ?? event.dispatch_id ?? event.gate_pass_id ?? null;
}

function fmtQtl(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${n.toFixed(2)} qtl` : "";
}

function fmtInr(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `₹${n.toLocaleString("en-IN")}` : "";
}

function firstLine(text) {
  return String(text || "").split("\n")[0].trim();
}

/** Primary entity label: vehicle · party · document code */
function subjectLine(event) {
  const parts = [];
  const vehicle = String(event.vehicle_number || "").trim().toUpperCase();
  const party = String(event.party_name || "").trim();
  const code = String(
    event.code
      || event.purchase_code
      || event.gate_pass_code
      || event.entity_code
      || event.contract_number
      || event.grn_number
      || "",
  ).trim();

  if (vehicle && vehicle !== "WALKIN") parts.push(vehicle);
  if (party) parts.push(party);
  if (code) parts.push(code);
  else if (entityId(event) != null) parts.push(`#${entityId(event)}`);
  return parts.join(" · ");
}

function varietyLine(event) {
  const variety = String(event.variety || event.quality || "").trim();
  if (!variety) return "";
  const qty = event.quantity != null ? fmtQtl(event.quantity) : "";
  const rate = event.rate != null ? fmtInr(event.rate) : "";
  const bits = [variety];
  if (qty) bits.push(qty);
  if (rate) bits.push(`@ ${rate}/qtl`);
  return bits.join(" · ");
}

function weightLine(event) {
  const parts = [];
  const gross = fmtQtl(event.gross_weight_qtl);
  const tare = fmtQtl(event.tare_weight_qtl);
  const net = fmtQtl(event.net_weight_qtl ?? event.weight_qtl);
  if (gross) parts.push(`gross ${gross}`);
  if (tare) parts.push(`tare ${tare}`);
  if (net) parts.push(`net ${net}`);
  return parts.length ? parts.join(", ") : "";
}

function joinSentences(...parts) {
  return parts.map((p) => String(p || "").trim()).filter(Boolean).join(" ");
}

function urlPurchase(event, fallback = "/purchases") {
  const pid = purchaseId(event);
  return pid != null ? `/purchases/${pid}` : fallback;
}

function urlUnloading(event) {
  const pid = purchaseId(event);
  return pid != null ? withQuery("/unloading", "purchase", pid) : "/unloading";
}

function urlWeighbridge(event, module = "inward") {
  const base = `/weighbridge/${module}`;
  const gid = gatePassId(event);
  if (gid != null) return withQuery(base, "gate_pass", gid);
  const did = dispatchId(event);
  if (did != null) return withQuery(base, "dispatch", did);
  const pid = purchaseId(event) ?? (event.type === "purchase" ? event.id : null);
  return pid != null ? withQuery(base, "purchase", pid) : base;
}

function urlLab(event) {
  const pid = purchaseId(event) ?? event.id;
  return pid != null ? withQuery("/lab/truck", "purchase", pid) : "/lab/truck";
}

function urlGate(event, module) {
  const mod = module || gateModuleForPurpose(event.purpose);
  const base = `/gate/${mod}`;
  const gid = gatePassId(event);
  return gid != null ? withQuery(base, "pass", gid) : base;
}

function urlOutwardDispatch(event) {
  const did = dispatchId(event);
  if (did != null) return withQuery("/sales/link", "dispatch", did);
  return "/sales/link";
}

function urlVoucher(event) {
  const vid = event.id ?? event.voucher_id;
  if (vid != null) return withQuery("/purchase/vouchers", "voucher", vid);
  return "/purchase/vouchers";
}

function urlTally(event) {
  const kind = String(event.entity_kind || "").toLowerCase();
  const eid = event.entity_id ?? event.id;
  if (kind === "voucher" && eid != null) return withQuery("/purchase/vouchers", "voucher", eid);
  if (kind === "journal" && eid != null) return withQuery("/accounts/ledger", "journal", eid);
  return "/accounts/tally-approvals";
}

export function buildNotificationRoute(event) {
  const type = String(event.type || "").trim();
  const action = String(event.action || "").trim().toLowerCase();
  const state = String(event.state || "").toUpperCase();

  switch (type) {
    case "gate_pass":
      if (action === "created") {
        const purpose = String(event.purpose || "").toUpperCase();
        if (purpose === "OUTWARD_RICE") return urlGate(event, "outward");
        if (purpose === "INWARD_STORE") return urlGate(event, "store");
        return urlWeighbridge(event, "inward");
      }
      return urlGate(event);

    case "purchase":
      if (["started", "weigh_corrected", "mid_weigh_requested", "unloading"].includes(action)) {
        return urlWeighbridge(event, "inward");
      }
      if (action === "weighed") {
        const kind = String(event.kind || "").toLowerCase();
        if (kind === "gross" || state === "WEIGH_GROSS") return urlWeighbridge(event, "inward");
        if (kind === "mid" || state === "WEIGH_MID") return urlUnloading(event);
        return urlPurchase(event, "/accounts");
      }
      if (action === "assigned") {
        const atype = String(event.assignment_type || "").toUpperCase();
        return atype === "PRE_ARRIVAL_LAB" ? urlLab(event) : urlUnloading(event);
      }
      if (action === "relab_requested") return urlLab(event);
      if (action === "negotiated") {
        return state === "REJECTED" ? urlGate(event, "procurement") : urlUnloading(event);
      }
      if (action === "transitioned") {
        if (state === "LAB_PENDING") return urlLab(event);
        if (state === "BARGAINING") return urlPurchase(event);
        if (state === "APPROVED" || state === "UNLOADING") return urlUnloading(event);
        if (state === "ACCOUNTS_PENDING") return urlPurchase(event);
        if (state === "COMPLETED" || state === "REJECTED") return urlGate(event, "procurement");
        return urlPurchase(event);
      }
      if (["finalized", "refinalized", "settlement_updated", "assign_supplier", "emandi_updated"].includes(action)) {
        return urlPurchase(event);
      }
      if (["hold", "resumed", "unloading_destinations_updated", "unloading_weights_updated"].includes(action)) {
        return urlUnloading(event);
      }
      return urlPurchase(event);

    case "lab":
      if (action === "composite_report") return "/lab/composite";
      return urlLab(event);

    case "stack":
      return urlWeighbridge(event, "inward");

    case "outward_dispatch":
      if (["linked", "loaded"].includes(action)) return urlWeighbridge(event, "outward");
      if (action === "weighed") {
        const kind = String(event.kind || "").toLowerCase();
        return kind === "gross"
          ? withQuery("/sales/vouchers", "dispatch", dispatchId(event))
          : urlWeighbridge(event, "outward");
      }
      if (action === "completed") return urlGate(event, "outward");
      if (["provisional", "settlement_draft", "settlement_docs"].includes(action)) {
        return withQuery("/sales/vouchers", "dispatch", dispatchId(event));
      }
      return urlOutwardDispatch(event);

    case "contract":
      return event.id != null ? withQuery("/contracts", "contract", event.id) : "/contracts";

    case "sales_contract":
      return event.id != null ? withQuery("/sales-contracts", "contract", event.id) : "/sales-contracts";

    case "voucher":
      return urlVoucher(event);

    case "tally":
    case "tally_sync_job":
      return urlTally(event);

    case "store_grn":
      return event.gate_pass_id != null ? withQuery("/store/grn", "gate_pass", event.gate_pass_id) : "/store/grn";

    case "production_batch":
    case "production":
    case "production_foreman":
      return event.id != null ? withQuery("/production", "batch", event.id) : "/production";

    case "boiler_logging":
      return "/production/boiler";

    case "party":
      return event.id != null ? `/parties/${event.id}/ledger` : "/parties";

    case "bank_statement":
      return "/accounts/bank-reconciliation";

    case "background_job": {
      const meta = event.meta || {};
      if (meta.party_id) return `/parties/${meta.party_id}/ledger`;
      if (event.kind === "tds.refresh_all") return "/accounts/tds";
      if (String(event.kind || "").startsWith("tally.")) return "/accounts/ledger";
      return "/";
    }

    default:
      return "/";
  }
}

function gatePassCopy(event, action) {
  const subject = subjectLine(event);
  const purpose = event.purpose_label || humanize(event.purpose) || "Gate";
  const driver = String(event.driver_name || "").trim();

  if (action === "created") {
    const p = String(event.purpose || "").toUpperCase();
    let next = "Check yard register for details.";
    if (p === "INWARD_PADDY") next = "Start inward purchase and record gross weight at weighbridge.";
    else if (p === "INWARD_STORE") next = "Store keeper can inspect and post GRN when ready.";
    else if (p === "OUTWARD_RICE") next = "Sales can link contract; record tare when truck is empty.";
    else if (p === "VISITOR") next = "Visitor logged in at gate.";
    return {
      title: `${purpose} — vehicle entered yard`,
      description: joinSentences(
        subject,
        driver ? `Driver ${driver}.` : "",
        next,
      ),
    };
  }
  if (action === "exit_requested") {
    return {
      title: "Gate exit approval needed",
      description: joinSentences(subject, "Vehicle blocked from checkout — manager must review exit request."),
    };
  }
  if (action === "exit_reviewed") {
    return {
      title: "Gate exit reviewed",
      description: joinSentences(subject, "Manager reviewed exit — guard may allow vehicle out."),
    };
  }
  if (action === "exited") {
    return {
      title: "Vehicle left the mill",
      description: joinSentences(subject, "Gate pass checked out and truck exited the yard."),
    };
  }
  if (action === "updated") {
    return {
      title: "Gate pass updated",
      description: joinSentences(subject, "Gate entry details were changed."),
    };
  }
  return {
    title: "Gate pass update",
    description: joinSentences(subject, titleCase(humanize(action))),
  };
}

function purchaseCopy(event, action) {
  const subject = subjectLine(event);
  const variety = varietyLine(event) || String(event.variety || "").trim();
  const state = String(event.state || "").toUpperCase();
  const stateLabel = PURCHASE_STATE_LABEL[state] || humanize(state);

  if (action === "started") {
    return {
      title: "Inward purchase started",
      description: joinSentences(
        subject,
        variety ? `${variety}.` : "",
        "Truck is in the yard — record gross weight at weighbridge.",
      ),
    };
  }
  if (action === "weighed") {
    const kind = String(event.kind || "").toLowerCase();
    const wt = weightLine(event);
    if (kind === "gross") {
      return {
        title: "Gross weight recorded",
        description: joinSentences(subject, wt, "Route to lab or assign unloading supervisor."),
      };
    }
    if (kind === "mid") {
      return {
        title: "Mid weight recorded",
        description: joinSentences(subject, wt, "Resume unloading on the floor."),
      };
    }
    return {
      title: "Tare recorded — net weight set",
      description: joinSentences(subject, wt, "Purchase moved to accounts for voucher settlement."),
    };
  }
  if (action === "assigned") {
    const atype = String(event.assignment_type || "").toUpperCase();
    if (atype === "PRE_ARRIVAL_LAB") {
      return {
        title: "Truck sent to quality lab",
        description: joinSentences(subject, "Sample required before unloading."),
      };
    }
    return {
      title: "Truck routed to unloading floor",
      description: joinSentences(subject, "Supervisor can begin unload."),
    };
  }
  if (action === "transitioned") {
    return {
      title: `Purchase ${stateLabel || "state changed"}`,
      description: joinSentences(subject, variety ? `${variety}.` : "", `Status is now ${state.replaceAll("_", " ").toLowerCase()}.`),
    };
  }
  if (action === "negotiated") {
    if (state === "REJECTED") {
      return {
        title: "Purchase rejected after bargaining",
        description: joinSentences(subject, "Truck rejected — arrange gate exit."),
      };
    }
    return {
      title: "Purchase approved after bargaining",
      description: joinSentences(subject, "Bargaining complete — begin unloading."),
    };
  }
  if (action === "unloading" || action === "unloading_weights_updated" || action === "unloading_destinations_updated") {
    return {
      title: "Unloading updated on floor",
      description: joinSentences(subject, "Bags/qtl or destinations changed — check if mid or tare weigh is next."),
    };
  }
  if (action === "hold") {
    return {
      title: "Unloading put on hold",
      description: joinSentences(
        subject,
        event.hold_reason ? `Reason: ${event.hold_reason}.` : "",
        "Manager must resume, reject, or send for re-lab.",
      ),
    };
  }
  if (action === "resumed") {
    return {
      title: "Unloading resumed",
      description: joinSentences(subject, "Hold cleared — continue unload on the floor."),
    };
  }
  if (action === "relab_requested") {
    return {
      title: "Re-lab requested",
      description: joinSentences(subject, "Fresh lab sample needed after quality hold."),
    };
  }
  if (action === "finalized") {
    return {
      title: "Purchase accounts finalized",
      description: joinSentences(subject, "Material/freight vouchers posted — vehicle may exit when clear."),
    };
  }
  if (action === "refinalized") {
    return {
      title: "Settlement re-opened after weigh change",
      description: joinSentences(subject, "Weights changed post-finalize — accounts must review vouchers."),
    };
  }
  if (action === "settlement_updated") {
    return {
      title: "Purchase settlement adjusted",
      description: joinSentences(subject, "Accounts updated settlement figures or deductions."),
    };
  }
  if (action === "assign_supplier") {
    return {
      title: "Purchase assigned to supplier",
      description: joinSentences(subject, "Party changed to supplier during accounts review."),
    };
  }
  if (action === "party_weight_updated") {
    return {
      title: "Party-side weight updated",
      description: joinSentences(subject, "Party weighbridge slip weight saved for contract split."),
    };
  }
  if (action === "allocations_updated") {
    return {
      title: "Contract allocations updated",
      description: joinSentences(subject, "Linked purchase contracts or qty split changed."),
    };
  }
  if (action === "emandi_updated") {
    return {
      title: "e-Mandi / payment details updated",
      description: joinSentences(subject, "Bank, invoice or mandi form fields saved."),
    };
  }
  if (action === "weigh_corrected") {
    return {
      title: "Weighbridge correction",
      description: joinSentences(subject, weightLine(event), "Bridge weight corrected — review accounts if needed."),
    };
  }
  if (action === "mid_weigh_requested") {
    return {
      title: "Mid-weigh requested",
      description: joinSentences(subject, "Partial unload done — record mid weight on weighbridge."),
    };
  }
  return {
    title: `Purchase ${humanize(action)}`,
    description: joinSentences(subject, stateLabel ? `Now ${stateLabel}.` : ""),
  };
}

function outwardCopy(event, action) {
  const subject = subjectLine(event);
  const contract = String(event.sales_contract_number || "").trim();
  const state = String(event.state || "").toUpperCase();
  const stateLabel = OUTWARD_STATE_LABEL[state] || humanize(state);

  if (action === "linked") {
    return {
      title: "Outward sales linked",
      description: joinSentences(
        subject,
        contract ? `Contract ${contract}.` : "",
        "Record empty-truck tare on weighbridge.",
      ),
    };
  }
  if (action === "weighed") {
    const kind = String(event.kind || "").toLowerCase();
    const wt = weightLine(event);
    if (kind === "gross") {
      return {
        title: "Outward gross weight recorded",
        description: joinSentences(subject, wt, "Issue or finalize sales voucher in accounts."),
      };
    }
    return {
      title: "Outward tare recorded",
      description: joinSentences(subject, wt, "Confirm loading on dispatch desk."),
    };
  }
  if (action === "loaded") {
    const bags = event.bags_loaded != null ? `${event.bags_loaded} bags loaded` : "Loading complete";
    return {
      title: "Outward truck loaded",
      description: joinSentences(subject, bags + ".", "Record loaded gross weight on weighbridge."),
    };
  }
  if (action === "completed") {
    return {
      title: "Outward dispatch finalized",
      description: joinSentences(subject, "Sales voucher issued — vehicle may exit gate."),
    };
  }
  if (action === "provisional") {
    return {
      title: "Provisional sales booked",
      description: joinSentences(subject, "Provisional Tally invoice created at mill gate."),
    };
  }
  if (action === "settlement_draft" || action === "settlement_docs") {
    return {
      title: "Sales settlement updated",
      description: joinSentences(subject, "Settlement figures or supporting documents saved."),
    };
  }
  if (action === "reopened") {
    return {
      title: "Outward settlement re-opened",
      description: joinSentences(subject, "Accounts reversed finalize — review vouchers again."),
    };
  }
  if (action === "empty_exit") {
    const count = event.dispatch_count ?? (Array.isArray(event.ids) ? event.ids.length : 0);
    return {
      title: "Outward truck left without loading",
      description: joinSentences(
        subject,
        count ? `${count} dispatch record(s) cancelled.` : "Open dispatch cancelled.",
        "Vehicle exited yard empty.",
      ),
    };
  }
  return {
    title: `Outward dispatch ${humanize(action)}`,
    description: joinSentences(subject, stateLabel ? `Status: ${stateLabel}.` : ""),
  };
}

function voucherCopy(event, action) {
  const subject = subjectLine(event);
  const vtype = VOUCHER_TYPE_LABEL[String(event.voucher_type || "").toUpperCase()] || humanize(event.voucher_type);
  const amount = fmtInr(event.amount);

  if (action === "created") {
    return {
      title: `${vtype || "Voucher"} created`,
      description: joinSentences(subject, amount, event.purchase_code ? `For purchase ${event.purchase_code}.` : ""),
    };
  }
  if (action === "updated") {
    return {
      title: `${vtype || "Voucher"} updated`,
      description: joinSentences(subject, amount, "Accounts amended voucher lines or amounts."),
    };
  }
  if (action === "freight_finalized") {
    return {
      title: "Freight voucher finalized",
      description: joinSentences(subject, amount, "Ready to post freight to Tally if required."),
    };
  }
  if (action === "cash_issued") {
    return {
      title: "Cash payment issued",
      description: joinSentences(subject, amount, "Cash voucher recorded against payable."),
    };
  }
  if (action === "cash_receipt") {
    return {
      title: "Cash receipt recorded",
      description: joinSentences(subject, amount, "Cash received and logged in ledger."),
    };
  }
  if (action === "cancelled") {
    return {
      title: "Voucher cancelled in Tally",
      description: joinSentences(subject, "Cancellation sent to Tally bridge."),
    };
  }
  return {
    title: `Voucher ${humanize(action)}`,
    description: joinSentences(subject, amount),
  };
}

function salesContractCopy(event, action) {
  const num = event.contract_number || `#${event.id}`;
  const buyer = String(event.party_name || "").trim();
  const product = varietyLine(event);
  const status = String(event.status || "").trim();

  if (action === "created") {
    return {
      title: `Sales contract ${num} registered`,
      description: joinSentences(buyer, product, status ? `Status ${status}.` : ""),
    };
  }
  if (action === "updated") {
    return {
      title: `Sales contract ${num} updated`,
      description: joinSentences(buyer, product, "Qty, rate or terms were changed."),
    };
  }
  if (action === "closed") {
    return {
      title: `Sales contract ${num} closed`,
      description: joinSentences(buyer, product, "Contract marked complete or closed."),
    };
  }
  if (action === "deleted") {
    return {
      title: `Sales contract ${num} deleted`,
      description: joinSentences(buyer, "Contract removed from active register."),
    };
  }
  return {
    title: `Sales contract ${humanize(action)}`,
    description: joinSentences(buyer, product),
  };
}

function contractCopy(event, action) {
  const num = event.contract_number || `#${event.id}`;
  const party = String(event.party_name || "").trim();
  const product = varietyLine(event);

  if (action === "created") {
    return { title: `Purchase contract ${num} created`, description: joinSentences(party, product) };
  }
  if (action === "closed") {
    return { title: `Purchase contract ${num} closed`, description: joinSentences(party, product, "Qty fully received or manually closed.") };
  }
  if (action === "updated") {
    return { title: `Purchase contract ${num} updated`, description: joinSentences(party, product) };
  }
  return { title: `Purchase contract ${humanize(action)}`, description: joinSentences(party, product) };
}

function tallyCopy(event, action) {
  const code = event.entity_code || event.code || "";
  const kind = humanize(event.entity_kind || "record");
  const count = Number(event.count || 0);

  if (action === "agent_connected") {
    const n = Number(event.agent_count || 1);
    return {
      title: "Tally agent connected",
      description: `Windows Tally bridge is online (${n} agent${n === 1 ? "" : "s"}). Vouchers can sync to Tally Prime.`,
    };
  }
  if (action === "agent_disconnected") {
    return {
      title: "Tally agent disconnected",
      description: "Tally bridge went offline — start the local agent to resume voucher sync.",
    };
  }
  if (action === "sync_bulk_queued" || action === "sync_queued") {
    return {
      title: count > 1 ? `${count} vouchers queued for Tally` : "Voucher queued for Tally",
      description: joinSentences(
        code ? `${code}` : "",
        count > 1 ? `${count} jobs sent to the Tally agent.` : "Sync job sent to the Tally agent.",
      ),
    };
  }
  if (action === "sync_result") {
    const msg = firstLine(event.tally_error_message || event.message);
    if (event.success === false) {
      return {
        title: code ? `Tally sync failed — ${code}` : "Tally sync failed",
        description: joinSentences(kind, msg || "Check Tally agent logs and voucher mapping."),
      };
    }
    return {
      title: code ? `${code} synced to Tally` : "Tally sync completed",
      description: joinSentences(
        kind,
        event.tally_sync_status ? `Status: ${event.tally_sync_status}.` : "",
        msg || "Posted successfully in Tally Prime.",
      ),
    };
  }
  if (action === "sync_rejected") {
    const reason = firstLine(event.rejection_reason || event.message);
    return {
      title: code ? `${code} rejected for Tally` : "Tally voucher rejected",
      description: joinSentences(reason || "Account Manager rejected this voucher.", "Correct it in Accounts, then resubmit."),
    };
  }
  if (action === "sync_approved") {
    return {
      title: code ? `${code} approved for Tally` : "Tally voucher approved",
      description: joinSentences(count > 1 ? `${count} vouchers approved.` : "Account Manager approved sync."),
    };
  }
  return {
    title: "Tally update",
    description: joinSentences(code, humanize(action)),
  };
}

function storeGrnCopy(event, action) {
  const subject = subjectLine(event);
  const grn = event.grn_number || (event.grn_id ? `GRN #${event.grn_id}` : "");
  const po = event.po_number ? `PO ${event.po_number}` : "";

  if (action === "submitted") {
    return {
      title: grn ? `${grn} posted to stock` : "Store GRN posted",
      description: joinSentences(subject, po, "Goods received into store inventory — tare weigh pending."),
    };
  }
  if (action === "weighed") {
    const kind = String(event.kind || "").toLowerCase();
    return {
      title: kind === "tare" ? "Store truck tare recorded" : "Store truck gross recorded",
      description: joinSentences(subject, grn, weightLine(event)),
    };
  }
  return {
    title: "Store GRN update",
    description: joinSentences(subject, grn, po),
  };
}

function labCopy(event, action) {
  const subject = subjectLine(event);
  if (action === "truck_report") {
    const state = String(event.state || "").toUpperCase();
    if (state === "BARGAINING") {
      return {
        title: "Lab flagged quality issue",
        description: joinSentences(subject, "Parameters out of range — bargaining decision needed."),
      };
    }
    return {
      title: "Lab passed truck sample",
      description: joinSentences(subject, "Quality approved — proceed with unloading."),
    };
  }
  if (action === "composite_report") {
    return { title: "Composite lab report saved", description: "Post-tare composite sample recorded." };
  }
  if (action === "sample_not_received") {
    return { title: "Lab skipped — no sample", description: joinSentences(subject, "Truck may proceed without lab report.") };
  }
  return { title: `Lab ${humanize(action)}`, description: subject };
}

function backgroundJobCopy(event, action) {
  const title = event.title || "Background task";
  const message = firstLine(event.message || event.error || "");
  if (action === "started") {
    return {
      title: `${title} started`,
      description: message || "Running in background — you will be notified when done.",
    };
  }
  if (action === "completed") {
    return {
      title: `${title} completed`,
      description: message || "Task finished successfully.",
    };
  }
  if (action === "failed") {
    return {
      title: `${title} failed`,
      description: message || "Open the related screen for details.",
    };
  }
  return { title, description: message };
}

function buildNotificationCopy(event) {
  const type = String(event.type || "").trim();
  const action = String(event.action || "").trim().toLowerCase();

  switch (type) {
    case "gate_pass":
      return gatePassCopy(event, action);
    case "purchase":
      return purchaseCopy(event, action);
    case "outward_dispatch":
      return outwardCopy(event, action);
    case "voucher":
      return voucherCopy(event, action);
    case "sales_contract":
      return salesContractCopy(event, action);
    case "contract":
      return contractCopy(event, action);
    case "tally":
    case "tally_sync_job":
      return tallyCopy(event, action);
    case "background_job":
      return backgroundJobCopy(event, action);
    case "store_grn":
      return storeGrnCopy(event, action);
    case "lab":
      return labCopy(event, action);
    case "stack":
      return {
        title: "Unloading recorded on floor",
        description: joinSentences(subjectLine(event), "Bags/qtl saved — mid or tare weigh may be next."),
      };
    default:
      return {
        title: titleCase(`${humanize(type)} ${humanize(action)}`.trim()),
        description: subjectLine(event) || "Activity recorded in the mill system.",
      };
  }
}

/**
 * @returns {null | {title:string, description:string, route:string, iconKey:string, tone:string, eventType:string, eventId:number|string|null}}
 */
export function describeEvent(event) {
  if (!event || typeof event !== "object") return null;
  const type = String(event.type || "").trim();
  const action = String(event.action || "").trim().toLowerCase();
  if (!type || NOISE_TYPES.has(type)) return null;
  if (!TYPE_META[type]) return null;
  if ((type === "tally" || type === "tally_sync_job") && TALLY_NOISE_ACTIONS.has(action)) {
    return null;
  }

  const meta = TYPE_META[type];

  const { title, description } = buildNotificationCopy(event);
  const route = buildNotificationRoute(event);

  return {
    title,
    description,
    route,
    iconKey: meta.iconKey,
    tone: event.success === false ? "danger" : meta.tone,
    eventType: type,
    eventId: entityId(event),
  };
}
